/**
 * Generic App Storage Hook
 * Provides save/load/delete functionality for IndexedDB
 * Eliminates need to duplicate storage.ts for each app
 */

import { useRef } from 'react';
import type { SavedPage } from '../database/db';

export interface SaveData<TInput = unknown, TResponse = unknown> extends Record<string, unknown> {
  inputs: TInput;
  results?: TResponse;
}

interface SavedCalculationIdentity {
  id: number | null;
  invalid: boolean;
}

function readSavedCalculationIdentity(): SavedCalculationIdentity {
  const rawCalcId = new URLSearchParams(window.location.search).get('calcId');
  if (rawCalcId === null) {
    return { id: null, invalid: false };
  }

  const id = Number(rawCalcId);
  if (!Number.isSafeInteger(id) || id <= 0) {
    return { id: null, invalid: true };
  }

  return { id, invalid: false };
}

function buildSavedCalculationURL(id: number): string {
  const url = new URL(window.location.href);
  url.search = '';
  url.searchParams.set('calcId', String(id));
  return url.toString();
}

/**
 * Generic hook to save/load app data to IndexedDB
 *
 * @param appName - Unique name for the app (e.g., 'iSteelElement', 'iSDOFSolver')
 * @param version - App version (e.g., '1.0.0')
 *
 * @example
 * const { saveDataToDB, deleteData, updateData } = useAppStorage<MyInput, MyResponse>('iMyApp', '1.0.0');
 */
export function useAppStorage<TInput = unknown, TResponse = unknown>(appName: string, version: string = '1.0.0') {
  const identity = useRef<SavedCalculationIdentity>(readSavedCalculationIdentity());

  const saveDataToDB = async (project: string, title: string, inputs: TInput, results?: TResponse, notes?: string) => {
    const { db } = await import('../database/db');

    if (identity.current.invalid) {
      throw new Error('Cannot save: the URL contains an invalid calculation ID.');
    }

    const savePayload: SaveData<TInput, TResponse> = {
      inputs,
      results
    };

    const currentId = identity.current.id;
    const existingPage = currentId === null ? null : await db.savedPages.get(currentId);

    if (currentId !== null && !existingPage) {
      throw new Error(`Cannot save: calculation ${currentId} does not exist.`);
    }

    if (existingPage && existingPage.appName !== appName) {
      throw new Error(`Cannot save: calculation ${currentId} belongs to ${existingPage.appName}, not ${appName}.`);
    }

    const timestamp = Date.now();
    const metadata: SavedPage['metadata'] = {
      ...existingPage?.metadata,
      version,
      userAgent: navigator.userAgent,
      ...(notes !== undefined ? { notes } : {})
    };

    if (currentId !== null) {
      const url = buildSavedCalculationURL(currentId);
      await db.savedPages.update(currentId, {
        project,
        url,
        title,
        data: savePayload,
        timestamp,
        metadata
      });
      window.history.replaceState(window.history.state, '', url);
      return currentId;
    }

    const newId = await db.savedPages.add({
      appName,
      project,
      url: window.location.href,
      title,
      data: savePayload,
      timestamp,
      metadata
    });

    identity.current = { id: newId, invalid: false };

    const url = buildSavedCalculationURL(newId);
    await db.savedPages.update(newId, { url });
    window.history.replaceState(window.history.state, '', url);
    return newId;
  };

  const deleteData = async (id: number) => {
    const { db } = await import('../database/db');
    await db.savedPages.delete(id);
  };

  const updateData = async (id: number, changes: Partial<SavedPage>) => {
    const { db } = await import('../database/db');
    await db.savedPages.update(id, changes);
  };

  return { saveDataToDB, deleteData, updateData };
}
